import { useEffect, useState } from "react";
import api from "@/shared/lib/api";
import { Map, X, CheckCircle2, Circle, MapPin, LayoutGrid, Hammer } from "lucide-react";

/* Client-portal floorplan — status, photos & progress ONLY.
   No team members, no site measurements, no design version history.
   All thumbnails are view-only (never open the underlying file).
   Pin colours follow the company convention: walls orange, glass blue. */

const fileSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const isImage = (u) => /\.(png|jpe?g|webp|gif)(\?|$)/i.test(u || "");

const KIND_PIN = {
  wall:        { pin: "text-orange-600 fill-orange-300", label: "Wall" },
  glass:       { pin: "text-sky-600 fill-sky-300",       label: "Glass" },
  others:      { pin: "text-violet-600 fill-violet-300", label: "Other" },
  signage:     { pin: "text-emerald-600 fill-emerald-300", label: "Signage" },
  interactive: { pin: "text-rose-600 fill-rose-300",     label: "Interactive Wall" },
};
const pinKind = (k) => KIND_PIN[k] || KIND_PIN.wall;
// custom colour groups chosen for "Others" pins (matches the team floorplan)
const OTHER_PIN = {
  violet: "text-violet-600 fill-violet-300", fuchsia: "text-fuchsia-600 fill-fuchsia-300",
  teal: "text-teal-600 fill-teal-300", lime: "text-lime-600 fill-lime-300",
  indigo: "text-indigo-600 fill-indigo-300", cyan: "text-cyan-600 fill-cyan-300",
  yellow: "text-yellow-500 fill-yellow-300", stone: "text-stone-600 fill-stone-400",
};
const pinClass = (pin) =>
  (pin.kind === "others" && OTHER_PIN[pin.color]) || pinKind(pin.kind).pin;

const STAGE_CHIP = {
  completed:     { label: "Completed",       chip: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  in_production: { label: "In production",   chip: "bg-orange-100 text-orange-700 border-orange-300" },
  awaiting_you:  { label: "Awaiting you",    chip: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:      { label: "Design approved", chip: "bg-sky-100 text-sky-700 border-sky-300" },
  in_design:     { label: "In design",       chip: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

/* Segmented Overview / Floor Plan switcher used by the project detail page */
export function ClientViewTabs({ view, onChange }) {
  const Tab = ({ k, icon: Icon, label, testid }) => (
    <button onClick={() => onChange(k)} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-medium transition-all
        ${view === k
          ? "bg-zinc-900 text-white shadow-md"
          : "text-zinc-600 hover:bg-orange-50 hover:text-orange-700"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-orange-100 px-6 sm:px-10">
      <div className="max-w-5xl mx-auto flex items-center gap-2 py-2.5" data-testid="portal-view-tabs">
        <Tab k="overview" icon={LayoutGrid} label="Overview" testid="portal-tab-overview" />
        <Tab k="floorplan" icon={Map} label="Floor Plan" testid="portal-tab-floorplan" />
      </div>
    </div>
  );
}

function ViewOnlyThumb({ url, testid, tall }) {
  if (!url || !isImage(url)) return null;
  return (
    <div className="rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 select-none"
         onContextMenu={(e) => e.preventDefault()} data-testid={testid}>
      <img src={fileSrc(url)} alt="" draggable={false}
           onDragStart={(e) => e.preventDefault()}
           className={`w-full ${tall ? "h-36" : "h-24"} object-cover pointer-events-none`} />
    </div>
  );
}

function PhotoGrid({ title, urls, testid }) {
  const imgs = (urls || []).filter(isImage);
  if (!imgs.length) return null;
  return (
    <div className="mt-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5">
        {title} · {imgs.length}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {imgs.map((u, i) => <ViewOnlyThumb key={i} url={u} testid={`${testid}-${i}`} />)}
      </div>
    </div>
  );
}

function WallPanel({ code, boqId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/my-projects/${code}/floorplan/wall/${boqId}`)
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [code, boqId]);
  const meta = STAGE_CHIP[d?.stage] || STAGE_CHIP.in_design;
  const noteDone = d?.psm_note?.status === "completed";
  return (
    <div className="rounded-2xl border border-orange-100 bg-white shadow-lg p-5"
         data-testid="client-floorplan-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-zinc-900 truncate">
            {d?.wall?.label || "…"}
          </div>
          {d?.wall?.group && <div className="text-xs text-zinc-400">{d.wall.group}</div>}
        </div>
        <button onClick={onClose} data-testid="client-floorplan-panel-close"
                className="text-zinc-400 hover:text-zinc-700 shrink-0"><X className="w-5 h-5" /></button>
      </div>
      {!d ? (
        <div className="py-8 text-center text-zinc-400 text-sm">Loading…</div>
      ) : (
        <>
          <span className={`inline-flex items-center gap-1 mt-2 px-2.5 h-6 rounded-full text-[11px] font-medium border ${meta.chip}`}
                data-testid="client-floorplan-stage-chip">
            {meta.label}
          </span>

          {d.psm_note && (
            <div className={`mt-4 rounded-xl border p-3 ${noteDone
                  ? "border-emerald-200 bg-emerald-50/70" : "border-amber-300 bg-amber-50"}`}
                 data-testid="client-floorplan-psm-note">
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest mb-1
                              text-zinc-500">
                <Hammer className="w-3.5 h-3.5" /> Site note from our team
              </div>
              <div className="text-sm text-zinc-800">{d.psm_note.text}</div>
              {d.psm_note.photo && isImage(d.psm_note.photo) && (
                <div className="mt-2"><ViewOnlyThumb url={d.psm_note.photo} testid="client-floorplan-psm-note-photo" /></div>
              )}
              <div className={`mt-1.5 text-[11px] font-semibold ${noteDone ? "text-emerald-600" : "text-amber-700"}`}>
                {noteDone ? "✓ Resolved" : "Action needed"}
              </div>
            </div>
          )}

          <div className="mt-4 space-y-2" data-testid="client-floorplan-progress">
            {(d.progress || []).map((s) => (
              <div key={s.stage} className="flex items-center gap-2 text-sm">
                {s.done
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : <Circle className="w-4 h-4 text-zinc-300 shrink-0" />}
                <span className={s.done ? "text-zinc-800" : "text-zinc-500"}>{s.stage}</span>
                {s.value && <span className="ml-auto text-xs text-zinc-400">{s.value}</span>}
              </div>
            ))}
          </div>

          {d.design_thumb && isImage(d.design_thumb) && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5">Your design</div>
              <ViewOnlyThumb url={d.design_thumb} testid="client-floorplan-design-thumb" tall />
            </div>
          )}
          <PhotoGrid title="Site photos" urls={d.site_photos} testid="client-floorplan-site-thumb" />
          <PhotoGrid title="Installed photos" urls={d.install_photos} testid="client-floorplan-install-thumb" />
        </>
      )}
    </div>
  );
}

export default function ClientFloorplan({ code, data: preloaded }) {
  const [data, setData] = useState(preloaded || null);
  const [active, setActive] = useState(null);
  const [floorId, setFloorId] = useState("");

  useEffect(() => {
    if (preloaded) { setData(preloaded); return; }
    api.get(`/my-projects/${code}/floorplan`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [code, preloaded]);

  const floors = data?.floors?.length ? data.floors
    : (data?.floorplan?.image_url ? [{ id: "floor-1", name: "Floor 1", image_url: data.floorplan.image_url }] : []);
  const floor = floors.find((f) => f.id === floorId) || floors[0];
  if (!floor?.image_url) return null;
  const floorPins = (data?.pins || []).filter((p) => (p.floor_id || floors[0]?.id) === floor.id);
  const kindsPresent = [...new Set(floorPins.map((p) => p.kind || "wall"))];

  return (
    <div className="px-6 sm:px-10 py-8" data-testid="client-floorplan-section">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <div className="flex items-center gap-2">
            <Map className="w-4 h-4 text-orange-600" />
            <h2 className="font-display text-lg font-bold tracking-tight text-zinc-900">Floor Plan</h2>
            <span className="text-xs text-zinc-400">· tap a pin to see its progress</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            {kindsPresent.map((k) => (
              <span key={k} className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                <MapPin size={13} className={pinKind(k).pin} /> {pinKind(k).label}
              </span>
            ))}
          </div>
        </div>
        {floors.length > 1 && (
          <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="client-floorplan-floor-tabs">
            {floors.map((fl) => (
              <button key={fl.id} onClick={() => { setFloorId(fl.id); setActive(null); }}
                data-testid={`client-floorplan-floor-tab-${fl.id}`}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium border transition-colors ${fl.id === floor.id ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
                {fl.name}
              </button>
            ))}
          </div>
        )}
        <div className={`grid gap-5 items-start ${active ? "lg:grid-cols-[1fr_330px]" : ""}`}>
          <div className="rounded-2xl border border-orange-100 bg-white overflow-auto shadow-sm select-none"
               onContextMenu={(e) => e.preventDefault()}>
            <div className="relative inline-block min-w-full">
              <img src={fileSrc(floor.image_url)} alt="Floorplan" draggable={false}
                   onDragStart={(e) => e.preventDefault()}
                   className="block w-full pointer-events-none" data-testid="client-floorplan-image" />
              {floorPins.map((pin) => {
                const pk = pinKind(pin.kind);
                const isActive = active === pin.boq_id;
                return (
                  <button key={pin.id} onClick={() => setActive(isActive ? null : pin.boq_id)}
                          data-testid={`client-floorplan-pin-${pin.id}`}
                          style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                          className="absolute -translate-x-1/2 -translate-y-full group">
                    {isActive && (
                      <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[55%] w-7 h-7 rounded-full ring-2 ring-zinc-900 ring-offset-1 pointer-events-none" />
                    )}
                    <MapPin size={isActive ? 20 : 17} strokeWidth={isActive ? 2.6 : 2.2}
                            className={`drop-shadow ${pinClass(pin)} ${isActive ? "scale-110" : ""}`} />
                    <span className="absolute left-1/2 -translate-x-1/2 top-4 whitespace-nowrap text-[10px] font-medium bg-zinc-900 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {pin.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {active && <WallPanel code={code} boqId={active} onClose={() => setActive(null)} />}
        </div>
      </div>
    </div>
  );
}
